import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Head } from 'vite-react-ssg'
import { getCanonicalUrl } from '../utils/canonical'
import { FileText, Zap, ChevronDown, ChevronUp, Shield, Eye } from 'lucide-react'
import { AccessibilityStatementWidget } from '../components/AccessibilityStatement'

export default function AccessibilityStatementPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const toggleFaq = (i: number) => setOpenFaq(openFaq === i ? null : i)

  const requirements = [
    {
      region: 'European Union (EN 301 549 / Web Accessibility Directive)',
      requirement: 'Public sector bodies must publish an accessibility statement and review it annually. The statement must follow the W3C model and include a feedback mechanism.',
      mandatory: true
    },
    {
      region: 'United Kingdom (Public Sector Bodies Accessibility Regulations)',
      requirement: 'UK public sector organizations must publish an accessibility statement and respond to accessibility requests within 20 working days.',
      mandatory: true
    },
    {
      region: 'United States (Section 508)',
      requirement: 'Federal agencies must provide an accessibility conformance report (ACR/VPAT). An accessibility statement is strongly recommended for all government and many private sector sites.',
      mandatory: false
    },
    {
      region: 'Canada (AODA / ACA)',
      requirement: 'Ontario\'s AODA and the federal Accessible Canada Act require accessibility plans and progress reports. An accessibility statement supports compliance documentation.',
      mandatory: false
    },
    {
      region: 'Private Sector (all regions)',
      requirement: 'While not always legally mandatory, publishing an accessibility statement demonstrates good faith and significantly reduces legal risk in ADA/AODA/EAA lawsuits and complaints.',
      mandatory: false
    }
  ]

  const mustInclude = [
    'The scope of the statement (which website or product it covers)',
    'Conformance status (fully conformant, partially conformant, or non-conformant)',
    'Which WCAG version and level you target (e.g., WCAG 2.2 Level AA)',
    'Known accessibility limitations and available workarounds',
    'Contact information for reporting accessibility barriers',
    'A feedback mechanism and expected response time',
    'Date of the last accessibility assessment',
    'How users can request accessible alternatives for inaccessible content'
  ]

  const faqs = [
    {
      question: 'What is an accessibility statement?',
      answer: 'An accessibility statement is a public declaration by a website owner that describes: their commitment to accessibility, the standard they aim to meet (usually WCAG 2.1 or 2.2 Level AA), the current conformance status of their site, any known accessibility limitations, and how users can contact them to report problems or request accessible alternatives.'
    },
    {
      question: 'Is an accessibility statement legally required?',
      answer: 'In many jurisdictions it is mandatory. In the EU, the Web Accessibility Directive (WAD) requires public sector bodies to publish accessibility statements following the W3C model. In the UK, the Public Sector Bodies Accessibility Regulations have the same requirement. In the US, Section 508 requires conformance reports for federal agencies. Even where not legally mandatory, publishing a statement is strongly recommended as it demonstrates good faith and reduces legal risk.'
    },
    {
      question: 'What is the difference between an accessibility statement and a VPAT/ACR?',
      answer: 'A VPAT (Voluntary Product Accessibility Template) or ACR (Accessibility Conformance Report) is a detailed technical document that maps every WCAG success criterion to a conformance level for a specific product. It is primarily used in procurement contexts, particularly for US federal contracts. An accessibility statement is a shorter, user-facing document explaining your overall conformance status and how to get help. Both can coexist.'
    },
    {
      question: 'How often should I update my accessibility statement?',
      answer: 'At minimum, update your statement whenever: you make significant changes to your website, you complete a new accessibility audit, you resolve previously listed known issues, or you discover new accessibility problems. The EU Web Accessibility Directive requires annual review. As a general rule, treat your accessibility statement as a living document and review it at least quarterly.'
    },
    {
      question: 'Where should I publish my accessibility statement?',
      answer: 'Your accessibility statement should be easy to find. Best practices include: linking to it in your website footer (on every page), including a link in your privacy policy or legal section, making it accessible from your main navigation or a "Legal" section, and using a clear URL like /accessibility-statement or /accessibility. In the EU, the statement must be accessible to everyone, including assistive technology users.'
    },
    {
      question: 'Can I claim "fully conformant" if I haven\'t done a full audit?',
      answer: 'No. Claiming full conformance without proper testing creates significant legal risk. If your site has not been formally audited against WCAG 2.1 or 2.2 by a qualified professional or through thorough self-testing, you should claim "partially conformant" and list known issues. An honest partial conformance statement is legally much safer than an inaccurate full conformance claim.'
    },
    {
      question: 'What should I do if a user reports an accessibility issue I didn\'t know about?',
      answer: 'Respond promptly (ideally within the timeframe stated in your statement). Acknowledge the issue, provide an accessible alternative if you can, and log the issue with a target resolution date. Update your accessibility statement to include the newly discovered known issue. Treating reports as valuable feedback — not complaints — is both the right approach legally and ethically.'
    }
  ]

  return (
    <>
      <Head>
        <title>Accessibility Statement Generator — Free WCAG Template | achecker.ca</title>
        <meta
          name="description"
          content="Generate a free, WCAG-compliant accessibility statement for your website in minutes. Follows the W3C model. Download as plain text or HTML."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={getCanonicalUrl('/accessibility-statement-generator')} />
        <meta property="og:title" content="Free Accessibility Statement Generator — WCAG Compliant" />
        <meta property="og:description" content="Create a legally compliant accessibility statement in minutes. Follows the W3C model. Free, no login, download as HTML or text." />
        <meta property="og:type" content="website" />
      </Head>

      <div className="space-y-12">
        {/* Hero */}
        <section className="text-center py-12 bg-gradient-to-br from-purple-50 to-violet-100 rounded-2xl">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-6">
            <FileText className="w-8 h-8 text-purple-600" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Accessibility Statement Generator
          </h1>
          <p className="text-xl text-gray-600 mb-4 max-w-3xl mx-auto px-4">
            Generate a professionally structured, WCAG-compliant accessibility statement for your website in under 5 minutes.
            Follows the official <strong>W3C model</strong> required by EU law and global best practices.
          </p>
          <p className="text-base text-gray-500 mb-8 max-w-2xl mx-auto px-4">
            Free, no login required. Download as plain text or HTML and publish directly to your site.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#statement-generator"
              className="inline-flex items-center justify-center px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
              onClick={(e) => {
                e.preventDefault()
                document.getElementById('statement-generator')?.scrollIntoView({ behavior: 'smooth' })
              }}
            >
              <Zap className="w-5 h-5 mr-2" />
              Generate My Statement
            </a>
            <Link
              to="/wcag-validator"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-gray-700 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              <Shield className="w-5 h-5 mr-2" />
              Check WCAG Conformance
            </Link>
          </div>
        </section>

        {/* Widget */}
        <AccessibilityStatementWidget />

        {/* Legal Requirements */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Legal Requirements by Region
          </h2>
          <p className="text-gray-700 mb-6">
            Accessibility statement requirements vary by jurisdiction. Here is an overview of the key requirements:
          </p>
          <div className="space-y-4">
            {requirements.map((req, i) => (
              <div key={i} className="bg-white rounded-lg p-5 border border-gray-200 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className={`flex-shrink-0 px-2.5 py-1 rounded text-xs font-bold ${
                    req.mandatory ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {req.mandatory ? 'MANDATORY' : 'RECOMMENDED'}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{req.region}</h3>
                    <p className="text-sm text-gray-600">{req.requirement}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* What to include */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            What Every Accessibility Statement Must Include
          </h2>
          <div className="bg-white rounded-lg p-8 border border-gray-200 shadow-sm">
            <p className="text-gray-700 mb-5">
              Based on the <a href="https://www.w3.org/WAI/planning/statements/" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">W3C Accessibility Statement Generator</a> model and EU Web Accessibility Directive requirements, a complete statement must include:
            </p>
            <ul className="space-y-3">
              {mustInclude.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                    {i + 1}
                  </div>
                  <span className="text-gray-700 text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200">
                <button
                  onClick={() => toggleFaq(i)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                  aria-expanded={openFaq === i}
                  aria-controls={`faq-${i}`}
                >
                  <h3 className="text-lg font-medium text-gray-900 pr-4">{faq.question}</h3>
                  {openFaq === i
                    ? <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" aria-hidden="true" />
                    : <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" aria-hidden="true" />
                  }
                </button>
                {openFaq === i && (
                  <div id={`faq-${i}`} className="px-6 pb-4">
                    <p className="text-gray-700">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 bg-gradient-to-r from-purple-600 to-violet-600 rounded-2xl text-white">
          <h2 className="text-3xl font-bold mb-4">
            Know Your Conformance Status Before You Publish
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
            An honest accessibility statement requires knowing your actual conformance level. Run a full WCAG audit to identify real issues before writing your statement.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-purple-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              <Zap className="w-5 h-5 mr-2" />
              Run Full Accessibility Audit
            </Link>
            <Link
              to="/alt-text-generator"
              className="inline-flex items-center justify-center px-8 py-3 bg-purple-700 text-white rounded-lg font-semibold hover:bg-purple-800 transition-colors border border-purple-500"
            >
              <Eye className="w-5 h-5 mr-2" />
              Alt Text Generator
            </Link>
          </div>
        </section>
      </div>
    </>
  )
}
